import { config } from "../../../config";
import { getKismetClientId } from "../../../lib/utils/getKismetClientId";
import { getMonitoringService } from "../../../lib/monitoring";
import { SocketEventTypeEnum } from "../../../lib/socket/types/SocketEventTypeEnum";
export const PROFILE_STORAGE_KEY = `Kismet_UMP_${getKismetClientId()}`;

// Helper functions to get and update the profile
export function getProfile() {
  const profileJson = sessionStorage.getItem(PROFILE_STORAGE_KEY);
  return profileJson ? JSON.parse(profileJson) : null;
}

export function updateProfile(updates: any) {
  const currentProfile = getProfile() || {};
  const updatedProfile = { ...currentProfile, ...updates };
  sessionStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(updatedProfile));
  return updatedProfile;
}

export const requestOTP = async (
  profileId: string,
  method: "email" | "phone"
): Promise<void> => {
  // Get the contact info from profile object
  const profile = getProfile();
  const email = profile?.email;
  const phone = profile?.phoneNumber;

  const endpoint =
    method === "email"
      ? "/UserMarketingProfile/2fa/RequestMarketingEmailOTP"
      : "/UserMarketingProfile/2fa/RequestMarketingPhoneOTP";

  const requestBody = {
    profileId,
    email: method === "email" ? email : undefined,
    phoneNumber: method === "phone" ? phone : undefined,
  };

  console.log(`📤 Request ${method.toUpperCase()} OTP Request:`, {
    url: `${config.backend.base_url}${endpoint}`,
    body: requestBody,
    method,
  });

  const response = await fetch(`${config.backend.base_url}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  console.log("📥 Request OTP Response Status:", response);

  if (!response.ok) {
    throw new Error(`Failed to request OTP: ${response.status}`);
  }

  const data = await response.json();
  console.log("📥 Request OTP Response Data:", data);

  if (data.type === "ERROR") {
    throw new Error(
      data.error?.errorMessage || "Failed to send verification code"
    );
  }

  // Update the profile with session ID for email verification
  if (
    method === "email" &&
    data.type === "SUCCESS" &&
    data.success?.sessionId
  ) {
    updateProfile({ sessionId: data.success.sessionId });
  }
};

export const verifyEmailOTP = async (
  profileId: string,
  oneTimePassword: string
): Promise<void> => {
  // Get the email and sessionId from profile object
  const profile = getProfile();
  const email = profile?.email;
  const sessionId = profile?.sessionId;

  if (!sessionId) {
    throw new Error(
      "No session ID found. Please request a new verification code."
    );
  }

  if (!email) {
    throw new Error("No email found for verification.");
  }

  const requestBody = {
    profileId,
    oneTimePassword,
    email,
    sessionId,
  };

  console.log("🔍 Verify Email OTP Request:", requestBody);

  const response = await fetch(
    `${config.backend.base_url}/UserMarketingProfile/2fa/VerifyMarketingEmailOTP`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    }
  );

  console.log("🔍 Verify Email OTP Response:", response);

  if (!response.ok) {
    throw new Error(`Failed to verify email OTP: ${response.status}`);
  }

  const data = await response.json();
  if (data.type === "ERROR") {
    throw new Error(data.error?.errorMessage || "Invalid verification code");
  }

  // Clear the session ID after successful verification
  updateProfile({ sessionId: null });
};

export const verifyPhoneOTP = async (
  profileId: string,
  oneTimePassword: string
): Promise<void> => {
  // Get the phone number from profile object
  const profile = getProfile();
  const phone = profile?.phoneNumber;

  console.log("📱 Phone Verification Debug Info:", {
    profileId,
    phone,
    otpLength: oneTimePassword.length,
    storedData: {
      hasPhone: !!phone,
    },
  });

  if (!phone) {
    throw new Error("No phone number found for verification.");
  }

  const requestBody = {
    profileId,
    oneTimePassword,
    phoneNumber: phone,
  };

  console.log("🔍 Verify Phone OTP Request Details:", {
    url: `${config.backend.base_url}/UserMarketingProfile/2fa/VerifyMarketingPhoneOTP`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: requestBody,
  });

  try {
    const response = await fetch(
      `${config.backend.base_url}/UserMarketingProfile/2fa/VerifyMarketingPhoneOTP`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    console.log("🔍 Verify Phone OTP Response Status:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Phone Verification Error Response:", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(
        `Failed to verify phone OTP: ${response.status} - ${errorText}`
      );
    }

    const data = await response.json();
    console.log("📥 Phone Verification Response Data:", data);

    if (data.type === "ERROR") {
      console.error("❌ Phone Verification API Error:", {
        error: data.error,
        errorMessage: data.error?.errorMessage,
      });
      throw new Error(data.error?.errorMessage || "Invalid verification code");
    }

    // Log successful verification
    console.log("✅ Phone Verification Success:", {
      profileId,
      phone: phone.replace(/\d(?=\d{4})/g, "*"), // Mask phone number for logging
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("❌ Phone Verification Failed:", {
      error: error instanceof Error ? error.message : "Unknown error",
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// Legacy function for backward compatibility
export const verifyOTP = async (
  profileId: string,
  oneTimePassword: string,
  method: "email" | "phone"
): Promise<void> => {
  try {
    // For phone verification, bypass session ID check
    if (method === "phone") {
      await verifyPhoneOTP(profileId, oneTimePassword);
    } else {
      // For email, continue with existing verification
      await verifyEmailOTP(profileId, oneTimePassword);
    }

    // After successful verification, emit the offerSaved event
    const monitoringService = getMonitoringService();
    monitoringService.emitEvent(SocketEventTypeEnum.OFFER_SAVED, {
      metricName: "offerSaved",
      value: 1,
      profileId,
      verificationMethod: method,
      timestamp: new Date().toISOString(),
    });

    console.log(`✅ Offer saved event emitted after ${method} verification`, {
      profileId,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
};
